// src/components/profile/UsernameClaimModal.tsx
//
// First-login username claim. Shown when: user is registered,
// authenticated, NOT anonymous, AND has no username yet on their
// profile doc. Modal is non-dismissible — username is required for
// registered users so social features (follow, share-link) work.
//
// Anonymous users never see this modal — they can use the app
// without a username. If they later register, this modal opens
// after the upgrade.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useT } from '../../i18n/useT';
import { useAuthStore } from '../../stores/authStore';
import { usePublicProfile } from '../../profile/usePublicProfile';
import { claimUsername } from '../../profile/username';
import { validateUsername } from '../../profile/types';

/** Generate a friendly suggestion seeded on display name + a short
 *  random number. Doesn't guarantee availability — user will get
 *  an error and re-pick if it's taken. */
function suggestUsername(displayName: string | null | undefined): string {
  const base = (displayName || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  const seed = base.length >= 3 ? base.slice(0, 16) : 'traveler';
  // Always start with a letter (regex requires it).
  const head = /^[a-z]/.test(seed) ? seed : `t${seed}`;
  const suffix = Math.floor(100 + Math.random() * 900);
  return `${head}${suffix}`;
}

export const UsernameClaimModal: React.FC = () => {
  const t = useT();
  const { user, authMode } = useAuthStore();
  const { profile } = usePublicProfile(user?.uid ?? null);

  const isRegistered = authMode === 'registered';
  const hasUsername = !!profile?.username;
  const isAnonymous = !!user?.isAnonymous;

  const shouldShow =
    !!user && isRegistered && !isAnonymous && !!profile && !hasUsername;

  const initialSuggestion = useMemo(
    () => suggestUsername(user?.displayName ?? profile?.display_name ?? null),
    // We only seed once on first open. Don't churn on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.uid]
  );

  const [value, setValue] = useState(initialSuggestion);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset when the modal becomes relevant for a freshly-resolved user.
  useEffect(() => {
    if (shouldShow) {
      setValue(initialSuggestion);
      setError(null);
    }
  }, [shouldShow, initialSuggestion]);

  if (!shouldShow) return null;

  const handleClaim = async () => {
    setError(null);
    const v = validateUsername(value);
    if (!v.ok) {
      setError(t('Pick a 3–30 character username starting with a letter.'));
      return;
    }
    setSaving(true);
    try {
      const result = await claimUsername(value);
      if (result.ok !== true) {
        const fail = result as { ok: false; reason: string };
        if (fail.reason === 'taken') {
          setError(t('That username is taken. Try another.'));
        } else {
          setError(t('Could not claim that username.'));
        }
      }
      // On success: profile listener picks up new username, modal
      // hides itself via shouldShow returning false.
    } catch (err) {
      console.warn('UsernameClaimModal: claim failed:', err);
      setError(t('Could not claim that username.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>{t('Pick a username')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'Your username is how friends find and tag you. You can change it later.'
            )}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={error || ' '}
            disabled={saving}
            inputProps={{ maxLength: 30 }}
            InputProps={{
              startAdornment: (
                <Typography component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                  @
                </Typography>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleClaim}
          disabled={saving || !value.trim()}
        >
          {saving ? t('Claiming…') : t('Claim username')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
